import { cn } from '@/lib/utils';
import { Tooltip } from './tooltip';

export interface Tab {
  id: string;
  label: string;
  disabled?: boolean;
  /** Optional trailing count shown after the label (e.g. "5/57"). */
  count?: string;
  /** Hover hint — used when disabled to explain why (browsers suppress title on disabled buttons). */
  tooltip?: string;
  /** Small visual badge after the label (e.g. "Coming Soon"). Pair with disabled if inert. */
  badge?: string;
}

export interface TabsProps {
  tabs: Tab[];
  active: string;
  onChange: (id: string) => void;
  className?: string;
  /**
   * `sm` halves the vertical padding and drops a type step. For tab bars that live inside sticky
   * page chrome, where every pixel is taken off the surface below. @default 'default'
   */
  size?: 'default' | 'sm';
}

/** Per-size trigger metrics. Kept as whole class strings so Tailwind's scanner sees them. */
const SIZE_CLASSES: Record<NonNullable<TabsProps['size']>, string> = {
  default: 'px-5 py-2.5 text-base',
  sm: 'px-3 py-1.5 text-sm',
};

export function Tabs({ tabs, active, onChange, className, size = 'default' }: TabsProps) {
  return (
    <div className={cn('flex border-b-[1.6px] border-outlines', className)} role="tablist">
      {tabs.map((tab) => {
        const button = (
          <button
            key={tab.id}
            role="tab"
            aria-selected={active === tab.id}
            disabled={tab.disabled}
            title={tab.disabled ? undefined : tab.tooltip}
            onClick={() => !tab.disabled && onChange(tab.id)}
            className={cn(
              'inline-flex items-center font-medium transition-colors',
              SIZE_CLASSES[size],
              active === tab.id
                ? 'cursor-pointer border-b-[1.6px] border-primary font-semibold text-primary'
                : tab.disabled
                  ? 'cursor-not-allowed text-text-secondary opacity-40'
                  : 'cursor-pointer text-text-secondary hover:text-text-primary',
            )}
          >
            {tab.label}
            {tab.count != null && (
              <span
                className={cn(
                  'ml-2 text-xs font-medium',
                  active === tab.id ? 'text-primary' : 'text-text-placeholder',
                )}
              >
                {tab.count}
              </span>
            )}
            {tab.badge != null && (
              <span className="ml-2 rounded-full bg-outlines/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                {tab.badge}
              </span>
            )}
          </button>
        );

        return tab.tooltip ? (
          <Tooltip key={tab.id} text={tab.tooltip}>
            {button}
          </Tooltip>
        ) : (
          button
        );
      })}
    </div>
  );
}
